import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingEffectsMultiV16Params {
  /**
   * 输入参数
   */
  input: {
    /**
     * 输入图像URL列表，必须包含2个图像
     */
    images: string[];
    /**
     * 视频时长，必须为正数
     */
    duration: number;
  };
  /**
   * 特效场景，可选值：'hug'、'kiss'、'heart_gesture'、'fight'
   * @values hug | kiss | heart_gesture | fight
   */
  effect_scene: 'hug' | 'kiss' | 'heart_gesture' | 'fight';
}

export interface KlingEffectsMultiV16Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_effects_multi_v16
 *
 * Kling AI Multi Person Video Effects Generator (V1.6 Model)
 * 
 * Model: kling-v1-6
 * Features: 多人互动特效视频生成 (Multi Person Video Effects)
 * Effect Scenes: hug(拥抱), kiss(亲吻), heart_gesture(比心), fight(打架)
 * Input Images: 必须包含2个图像
 * 
 * API Documentation: https://app.klingai.com/cn/dev/document-api/apiReference/model/videoEffects
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingEffectsMultiV16 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingEffectsMultiV16({
 *   // 参数见 KlingEffectsMultiV16Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingEffectsMultiV16Params 接口定义
 * @returns 生成的资源列表
 */
export async function klingEffectsMultiV16(
  params: KlingEffectsMultiV16Params,
): Promise<KlingEffectsMultiV16Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_effects_multi_v16',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingEffectsMultiV16Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
